import { supabase } from '../lib/supabase';

export const auditService = {
  // Get audit logs with pagination and filters
  async getAuditLogs({
    page = 1,
    pageSize = 50,
    actionType = null,
    entityType = null,
    adminId = null,
    startDate = null,
    endDate = null,
    searchTerm = null
  } = {}) {
    let query = supabase?.from('admin_audit_logs')?.select(`
        *,
        user_profiles (
          id,
          full_name,
          email
        )
      `, { count: 'exact' })?.order('created_at', { ascending: false });
    
    // Apply filters (action_type column does not exist in DB, skip it)
    if (entityType) {
      query = query?.eq('entity_type', entityType);
    }
    
    if (adminId) {
      query = query?.eq('admin_id', adminId);
    }
    
    if (startDate) {
      query = query?.gte('created_at', startDate);
    }
    
    if (endDate) {
      query = query?.lte('created_at', endDate);
    }
    
    // Pagination
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query?.range(from, to);
    
    const { data, error, count } = await query;
    
    if (error) throw error;
    
    return {
      logs: data?.map(log => ({
        id: log?.id,
        adminId: log?.admin_id,
        adminName: log?.user_profiles?.full_name || 'Unknown',
        adminEmail: log?.user_profiles?.email || '',
        actionType: log?.entity_type,
        entityType: log?.entity_type,
        entityId: log?.entity_id,
        changes: log?.changes,
        ipAddress: log?.ip_address,
        userAgent: log?.user_agent,
        createdAt: log?.created_at
      })),
      totalCount: count,
      totalPages: Math.ceil(count / pageSize),
      currentPage: page
    };
  },

  // Log an admin action
  async logAction({
    actionType,
    entityType,
    entityId,
    changes,
    ipAddress = null,
    userAgent = null
  }) {
    const { data, error } = await supabase?.rpc('log_admin_action', {
      p_action_type: actionType,
      p_entity_type: entityType,
      p_entity_id: entityId,
      p_changes: changes,
      p_ip_address: ipAddress,
      p_user_agent: userAgent
    });
    
    if (error) throw error;
    return data;
  },

  // Get audit statistics
  async getAuditStatistics(days = 30) {
    const startDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000)?.toISOString();
    
    const { data, error } = await supabase?.from('admin_audit_logs')?.select('entity_type, created_at')?.gte('created_at', startDate);
    
    if (error) throw error;
    
    // Group by entity type
    const statsByAction = {};
    data?.forEach(log => {
      statsByAction[log.entity_type] = (statsByAction?.[log?.entity_type] || 0) + 1;
    });
    
    return {
      totalActions: data?.length,
      byActionType: statsByAction
    };
  },

  // Get recent admin activity
  async getRecentActivity(limit = 10) {
    const { data, error } = await supabase?.from('admin_audit_logs')?.select(`
        id,
        entity_type,
        created_at,
        user_profiles (
          full_name,
          email
        )
      `)?.order('created_at', { ascending: false })?.limit(limit);
    
    if (error) throw error;
    
    return data?.map(log => ({
      id: log?.id,
      actionType: log?.entity_type,
      entityType: log?.entity_type,
      adminName: log?.user_profiles?.full_name || 'Unknown',
      createdAt: log?.created_at
    }));
  },

  // Export audit logs as CSV
  async exportAuditLogs(filters = {}) {
    const result = await this.getAuditLogs({
      ...filters,
      page: 1,
      pageSize: 10000 // Get all for export
    });
    
    const csvHeader = 'Timestamp,Admin,Entity Type,Entity ID,Changes,IP Address\n';
    const csvRows = result?.logs?.map(log => {
      const timestamp = new Date(log.createdAt)?.toLocaleString();
      const admin = `${log?.adminName} (${log?.adminEmail})`;
      const changes = JSON.stringify(log?.changes || {})?.replace(/"/g, '""');
      
      return `"${timestamp}","${admin}","${log?.entityType}","${log?.entityId || ''}","${changes}","${log?.ipAddress || ''}"`;
    })?.join('\n');
    
    return csvHeader + csvRows;
  }
};